import React, { useState } from 'react'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from 'react-query'
import { useToast } from '@/contexts/ToastContext'
import { tierOf, TIER_OPTIONS } from '@/utils/advertiserTier'
import { ATAJOS, SIN_ATAJO, atajoActivo, type EstadoAtajo } from '@/utils/atajosCapturas'
import SaveCaptureModal from '@/components/SaveCaptureModal'
import {
  Flame,
  TrendingUp,
  Minus,
  TrendingDown,
  XCircle,
  Bookmark,
  Trash2,
  ExternalLink,
  Settings2,
  HardDrive,
  Clock,
  Sparkles,
  Search,
  Ban,
  Undo2,
  Check,
  RefreshCw
} from 'lucide-react'
import { capturedAdsApi } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import FireSparkline from '@/components/captured/FireSparkline'
import CapturedMedia from '@/components/captured/CapturedMedia'

import CapturedAdCard, { STATUS, TRIGGER_LABEL, type Status } from '@/components/captured/CapturedAdCard'

/** Los tipos que el clasificador puede devolver y que son objetivo del sistema. */
const KINDS = ['ebook', 'curso', 'plantilla', 'imprimible', 'membresia']

const SORTS = [
  { value: 'mega', label: 'Puntuación mega' },
  { value: 'velocity', label: 'Escalando más rápido' },
  { value: 'standout', label: 'Cuánto destaca en su nicho' },
  { value: 'advertiser', label: 'Tamaño del anunciante' },
  { value: 'longest', label: 'Más tiempo corriendo' },
  { value: 'kind', label: 'Tipo de producto' },
  { value: 'peak', label: 'Pico de copias' },
  { value: 'current', label: 'Copias ahora' },
  { value: 'recent', label: 'Vistos hace poco' },
  { value: 'captured', label: 'Capturados hace poco' }
]

/** En la papelera lo natural es deshacer lo ultimo, asi que ese orden va el primero. */
const ORDEN_DESCARTE = { value: 'dismissed', label: 'Descartados hace poco' }


/**
 * De lo que capturó el minero a lo que acabas viendo, tramo a tramo.
 *
 * Existe por una pregunta que no tenía respuesta en pantalla: el minero canta capturas y
 * este contador no se movía. En medio hay un clasificador que descarta lo que queda fuera
 * de objetivo —medido, 7 de cada 10— y un agrupado que junta en una tarjeta todos los
 * creativos del mismo anunciante. Cada tramo se abre con un clic, así que el número deja
 * de ser una afirmación y pasa a ser algo que se puede comprobar.
 *
 * Los números salen SIEMPRE del mismo universo, no del filtro activo: si no, abrir los
 * descartes descuadraría el embudo justo cuando se está usando para entenderlo.
 */
function Embudo({
  capturado,
  descartadas,
  pendientes,
  visibles,
  anunciantes,
  vista,
  onVer
}: {
  capturado: number
  descartadas: number
  pendientes: number
  visibles: number
  anunciantes?: number
  vista: Vista
  onVer: (que: Vista) => void
}) {
  if (!capturado) return null

  // Lo vivo que no es digital ni está sin clasificar: cabe en el hueco, no en un tramo.
  const otros = Math.max(0, capturado - descartadas - pendientes - visibles)

  // En el mismo orden que los pasos de arriba, para que el tramo ámbar caiga debajo de
  // "En la rejilla" y la barra se lea como lo que es: lo que queda al final del recorrido.
  const tramos = [
    { key: 'descartadas' as const, n: descartadas, color: '#3f3f46', label: 'descartadas' },
    { key: 'pendientes' as const, n: pendientes, color: '#57534e', label: 'sin veredicto' },
    { key: 'otros' as const, n: otros, color: '#292524', label: 'fuera del filtro de producto' },
    { key: 'visibles' as const, n: visibles, color: '#fbbf24', label: 'en la rejilla' }
  ].filter(t => t.n > 0)

  const pasos: Array<{
    key: Vista | 'capturado'
    valor: number
    label: string
    hint: string
    color?: string
  }> = [
    {
      key: 'capturado',
      valor: capturado,
      label: 'Capturado',
      hint: 'lo que el minero guardó'
    },
    {
      key: 'descartadas',
      valor: descartadas,
      // Este paso es la puerta a la papelera, no solo una cifra del recorrido: decía "fuera
      // de objetivo", que es lo que tira el clasificador, y no daba ninguna pista de que
      // ahí dentro estén los tuyos y se puedan recuperar.
      label: 'Descartado',
      hint: 'la papelera: los tuyos se recuperan'
    },
    {
      key: 'pendientes',
      valor: pendientes,
      label: 'Sin veredicto',
      hint: 'esperando al clasificador'
    },
    {
      key: 'visibles',
      valor: visibles,
      label: 'En la rejilla',
      hint: anunciantes != null ? `${anunciantes} anunciantes` : 'productos digitales vivos',
      color: '#fbbf24'
    }
  ]

  return (
    <div className="rounded-xl border border-stone-700 bg-stone-900 px-3 py-2">
      <div className="flex flex-wrap items-end gap-x-1 gap-y-3">
        {pasos.map((paso, i) => {
          const clicable = paso.key !== 'capturado' && paso.valor > 0
          const activo = paso.key === vista
          return (
            <React.Fragment key={paso.key}>
              {i > 0 && <span className="pb-4 text-stone-700">→</span>}
              <button
                type="button"
                disabled={!clicable}
                onClick={() => clicable && onVer(paso.key as Vista)}
                className={`flex-1 rounded-lg border px-2 py-1 text-left transition-colors ${
                  clicable
                    ? 'cursor-pointer border-stone-700 hover:border-stone-500 hover:bg-stone-800'
                    : 'cursor-default border-transparent'
                } ${activo ? 'border-stone-500 bg-stone-800' : ''}`}
                title={clicable ? `Ver ${paso.label.toLowerCase()}` : undefined}
              >
                <div className="text-xs uppercase tracking-wide text-stone-500">{paso.label}</div>
                <div
                  className="text-xl font-semibold tabular-nums"
                  style={{ color: paso.color || '#e7e5e4' }}
                >
                  {paso.valor.toLocaleString('es-ES')}
                </div>
                <div className="text-xs text-stone-500">{paso.hint}</div>
              </button>
            </React.Fragment>
          )
        })}
      </div>

      {/* La misma historia en una barra: cuánto de lo capturado llega al final. */}
      <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-stone-800">
        {tramos.map(t => (
          <div
            key={t.key}
            style={{ width: `${(t.n / capturado) * 100}%`, backgroundColor: t.color }}
            title={`${t.n.toLocaleString('es-ES')} ${t.label}`}
          />
        ))}
      </div>
    </div>
  )
}

/** Un mando del panel "Afinar", con la pregunta que contesta encima. */
function Afinado({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-stone-500">{label}</span>
      {children}
    </label>
  )
}

/** El aspecto de un desplegable del panel. Se escribia igual nueve veces. */
const CAMPO =
  'rounded-xl border border-stone-600 bg-stone-900 px-3 py-2 text-sm text-stone-100'

function StatTile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col justify-center rounded-xl border border-stone-700 bg-stone-900 px-3 py-2">
      <div className="truncate text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className="text-xl font-semibold tabular-nums text-stone-100">{value}</div>
      {hint && <div className="truncate text-xs text-stone-500" title={hint}>{hint}</div>}
    </div>
  )
}

/** Qué tramo del embudo se está mirando. Es el estado de los filtros, dicho en corto. */
type Vista = 'visibles' | 'pendientes' | 'descartadas'

/**
 * Las cuentas viewer (rol `viewer`) entran solo a esta tab y en modo lectura: sin
 * umbrales, sin guardar y sin descartar. Es la misma vista de siempre para el resto.
 */
export default function CapturedAdsPage() {
  const { user } = useAuth()
  const readOnly = user?.role === 'viewer'
  const { showSuccess, showError } = useToast()
  const queryClient = useQueryClient()
  /** La captura que se está guardando: abre el modal de carpeta y nota. */
  const [guardando, setGuardando] = useState<any>(null)
  const [status, setStatus] = useState('all')
  const [trigger, setTrigger] = useState('all')
  const [sortBy, setSortBy] = useState('velocity')
  const [product, setProduct] = useState('digital')
  /** Ver SOLO los descartes. Lo enciende el tramo "Descartado" del embudo. */
  const [soloDescartadas, setSoloDescartadas] = useState(false)
  /**
   * En la papelera, quedarse con los descartes propios.
   *
   * Por defecto si: los 3.972 del clasificador son ruido para quien busca el que tiro por
   * error, y ahogarian los 896 suyos. El interruptor existe porque el clasificador tambien
   * se equivoca y hay que poder mirarle la mano.
   */
  const [soloMios, setSoloMios] = useState(true)
  const [grouped, setGrouped] = useState(true)
  const [showSettings, setShowSettings] = useState(false)

  /**
   * El buscador consulta al servidor, así que se espera a que el usuario pare de teclear.
   * Sin esto, "recetas" son siete peticiones y las respuestas pueden llegar desordenadas.
   */
  const [busqueda, setBusqueda] = useState('')
  const [busquedaAplicada, setBusquedaAplicada] = useState('')
  React.useEffect(() => {
    const t = setTimeout(() => setBusquedaAplicada(busqueda.trim()), 350)
    return () => clearTimeout(t)
  }, [busqueda])

  // Filtros nuevos, los que de verdad acotan: qué vende, cuánto repite, cómo de grande es
  // el anunciante y cuánto lleva corriendo.
  const [kind, setKind] = useState('all')
  const [minCopies, setMinCopies] = useState('')
  const [minAdvertiserAds, setMinAdvertiserAds] = useState('')
  /** Copias ganadas por día. El filtro ya existía en la API y no estaba en pantalla. */
  const [minVelocity, setMinVelocity] = useState('')
  /** ¿Está abierto el panel de detalle? Empieza cerrado: la fila de arriba basta. */
  const [afinarAbierto, setAfinarAbierto] = useState(false)
  /** Solo lo capturado en las últimas N horas: la pregunta de quien entra cada día. */
  const [desdeHoras, setDesdeHoras] = useState('')
  const [minDays, setMinDays] = useState('')

  const POR_PAGINA = 40

  /**
   * El global de la app marca los datos frescos 5 minutos, así que al volver a esta pantalla
   * no se pedía nada y había que recargar a mano. Aquí no vale: el minero captura sin parar.
   *
   * `refetchOnMount: 'always'` + `staleTime: 0` revalidan al entrar, y como React Query
   * sigue mostrando lo cacheado mientras pide, la actualización es silenciosa: no hay
   * parpadeo ni pantalla vacía. El intervalo se pausa solo cuando la pestaña no se ve.
   */
  const revalidate = {
    staleTime: 0,
    refetchOnMount: 'always' as const,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false
  }

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    [
      'captured-ads',
      status, trigger, sortBy, product, grouped, busquedaAplicada,
      kind, minCopies, minAdvertiserAds, minDays, minVelocity, desdeHoras, soloDescartadas,
      soloDescartadas && soloMios
    ],
    ({ pageParam = 1 }) =>
      capturedAdsApi.list({
        status,
        trigger,
        sortBy,
        product,
        grouped,
        kind,
        ...(soloDescartadas ? { includeDismissed: 'only' as const } : {}),
        ...(soloDescartadas && soloMios ? { dismissedBy: 'usuario' as const } : {}),
        ...(soloDescartadas && soloMios ? { dismissedBy: 'usuario' as const } : {}),
        limit: POR_PAGINA,
        page: pageParam,
        ...(busquedaAplicada ? { q: busquedaAplicada } : {}),
        ...(minCopies ? { minCopies: Number(minCopies) } : {}),
        ...(minAdvertiserAds ? { minAdvertiserAds: Number(minAdvertiserAds) } : {}),
        ...(minVelocity ? { minVelocity: Number(minVelocity) } : {}),
        ...(desdeHoras ? { desdeHoras: Number(desdeHoras) } : {}),
        ...(minDays ? { minDays: Number(minDays) } : {})
      }),
    {
      getNextPageParam: (ultima: any) => {
        const p = ultima?.pagination
        return p && p.page < p.totalPages ? p.page + 1 : undefined
      },
      ...revalidate,
      /**
       * El refresco automático de una consulta infinita vuelve a pedir TODAS las páginas
       * cargadas. Arriba interesa —el minero captura sin parar— pero si has bajado seis
       * tandas serían seis peticiones cada minuto para leer algo que ya estás mirando.
       * Así que se refresca solo mientras estés en la primera.
       */
      refetchInterval: (d: any) => ((d?.pages?.length ?? 1) <= 1 ? 60_000 : false)
    }
  )

  // Con los mismos filtros que la rejilla: si no, la cabecera habla de un conjunto y las
  // tarjetas de otro, y el usuario ve dos números que no cuadran sin saber por qué.
  const { data: stats } = useQuery(
    ['captured-ads-stats', status, trigger, product, busquedaAplicada, kind, minCopies, minAdvertiserAds, minDays, minVelocity, desdeHoras, soloDescartadas, soloDescartadas && soloMios],
    () =>
      capturedAdsApi.stats({
        status,
        trigger,
        product,
        kind,
        ...(soloDescartadas ? { includeDismissed: 'only' as const } : {}),
        ...(busquedaAplicada ? { q: busquedaAplicada } : {}),
        ...(minCopies ? { minCopies: Number(minCopies) } : {}),
        ...(minAdvertiserAds ? { minAdvertiserAds: Number(minAdvertiserAds) } : {}),
        ...(minVelocity ? { minVelocity: Number(minVelocity) } : {}),
        ...(desdeHoras ? { desdeHoras: Number(desdeHoras) } : {}),
        ...(minDays ? { minDays: Number(minDays) } : {})
      }),
    revalidate
  )
  const { data: settingsData } = useQuery('captured-ads-settings', capturedAdsApi.getSettings, {
    enabled: showSettings
  })

  const invalidate = () => {
    queryClient.invalidateQueries('captured-ads')
    queryClient.invalidateQueries('captured-ads-stats')
  }

  /**
   * Echar al anunciante entero.
   *
   * Con el agrupado, una tarjeta puede tener diez creativos detrás, y descartarlos uno a
   * uno son diez clics para decir una sola cosa. Además, mientras quede uno vivo el minero
   * le sigue pidiendo el catálogo y gastando créditos en él.
   */
  const dismissAdvertiserMutation = useMutation(
    (pageId: string) => capturedAdsApi.dismissAdvertiser(pageId),
    {
      onSuccess: (r: any) => {
        showSuccess(r?.message || 'Anunciante descartado')
        invalidate()
      },
      onError: (e: any) => showError(e?.response?.data?.message || 'No se pudo descartar')
    }
  )

  /**
   * Deshacer un descarte.
   *
   * Descartar es un clic y equivocarse tambien. El aviso de vuelta dice si la ficha volvio
   * entera o aligerada, porque de los descartes viejos se tiro el creativo para ahorrar
   * espacio: vuelve el anuncio, no su video.
   */
  const restoreMutation = useMutation((id: string) => capturedAdsApi.restore(id), {
    onSuccess: (r: any) => {
      showSuccess(r?.message || 'Captura recuperada')
      invalidate()
    },
    onError: (e: any) => showError(e?.response?.data?.message || 'No se pudo recuperar')
  })

  const dismissMutation = useMutation((id: string) => capturedAdsApi.dismiss(id), {
    onSuccess: () => {
      showSuccess('Captura descartada')
      invalidate()
    },
    onError: () => {
      showError('No se pudo descartar')
    }
  })

  const settingsMutation = useMutation(
    (s: Record<string, number | boolean>) => capturedAdsApi.updateSettings(s),
    {
      onSuccess: () => {
        showSuccess('Umbrales actualizados. Se aplican en la próxima búsqueda.')
        queryClient.invalidateQueries('captured-ads-settings')
      },
      onError: () => {
        showError('No se pudieron guardar los umbrales')
      }
    }
  )

  /**
   * Las tandas se aplanan quitando repetidos por `_id`.
   *
   * Hace falta de verdad: el desplazamiento por página se calcula sobre un conjunto que
   * cambia mientras desplazas —el minero captura sin parar—, así que una captura nueva
   * empuja a las demás y alguna puede aparecer en dos tandas. El desempate por `_id` fija
   * el orden de una consulta, pero no impide que entren documentos nuevos por delante.
   */
  const items = React.useMemo(() => {
    const vistos = new Set<string>()
    const out: any[] = []
    for (const tanda of (data as any)?.pages || []) {
      for (const it of tanda?.data || []) {
        const id = String(it._id)
        if (vistos.has(id)) continue
        vistos.add(id)
        out.push(it)
      }
    }
    return out
  }, [data])

  /**
   * El total sale de la ÚLTIMA tanda, no de la primera.
   *
   * El minero captura mientras miras, así que el total que vino con la primera página ya
   * es viejo; usar ese dejaba a "Eso es todo: N" diciendo una cifra por debajo de las
   * tarjetas que realmente hay en pantalla.
   */
  /**
   * Los filtros puestos, cada uno con su forma de quitarse.
   *
   * Se deriva del estado en vez de mantenerse aparte: una lista paralela se desincroniza en
   * cuanto alguien añade un filtro y se olvida de registrarlo aquí.
   */
  /** El tramo del embudo que corresponde a los filtros puestos ahora mismo. */
  const vista: Vista = soloDescartadas ? 'descartadas' : product === 'pending' ? 'pendientes' : 'visibles'

  const verTramo = (que: Vista) => {
    const papelera = que === 'descartadas'
    setSoloDescartadas(papelera)
    setProduct(que === 'pendientes' ? 'pending' : papelera ? 'all' : 'digital')
    // La papelera se mira para deshacer, y para eso hace falta lo ultimo primero y cada
    // descarte por separado: agrupado por anunciante no se ve QUE tarjeta se tiro.
    if (papelera) {
      setSortBy(ORDEN_DESCARTE.value)
      setGrouped(false)
    } else {
      // Lo que se cambio al entrar se deshace al salir: si no, se vuelve a la rejilla
      // desagrupada sin haber tocado la casilla, y parece que se ha roto algo.
      setGrouped(true)
      if (sortBy === ORDEN_DESCARTE.value) setSortBy('velocity')
    }
  }

  const filtrosActivos = React.useMemo(() => {
    const f: Array<{ key: string; label: string; clear: () => void }> = []
    if (busquedaAplicada) f.push({ key: 'q', label: `«${busquedaAplicada}»`, clear: () => setBusqueda('') })
    if (product !== 'digital') f.push({
      key: 'product',
      label: product === 'all' ? 'todo lo capturado' : 'sin clasificar',
      clear: () => setProduct('digital')
    })
    if (soloDescartadas && soloMios) f.push({
      key: 'mios',
      label: 'descartados por mi',
      clear: () => setSoloMios(false)
    })
    if (soloDescartadas) f.push({
      key: 'descartadas',
      label: 'solo descartadas',
      clear: () => verTramo('visibles')
    })
    if (kind !== 'all') f.push({ key: 'kind', label: kind, clear: () => setKind('all') })
    if (minAdvertiserAds) {
      const t = tierOf(Number(minAdvertiserAds))
      f.push({
        key: 'adv',
        label: t ? `${t.label} (${minAdvertiserAds}+ anuncios)` : `${minAdvertiserAds}+ anuncios`,
        clear: () => setMinAdvertiserAds('')
      })
    }
    if (desdeHoras) f.push({
      key: 'nuevas',
      label: desdeHoras === '24' ? 'nuevas de hoy' : `últimas ${desdeHoras} h`,
      clear: () => setDesdeHoras('')
    })
    if (minVelocity) f.push({
      key: 'vel',
      label: `${minVelocity}+ copias/día`,
      clear: () => setMinVelocity('')
    })
    if (minCopies) f.push({ key: 'copies', label: `${minCopies}+ copias`, clear: () => setMinCopies('') })
    if (minDays) f.push({ key: 'days', label: `${minDays}+ días`, clear: () => setMinDays('') })
    if (status !== 'all') f.push({ key: 'status', label: STATUS[status as Status]?.label ?? status, clear: () => setStatus('all') })
    if (trigger !== 'all') f.push({ key: 'trigger', label: TRIGGER_LABEL[trigger] ?? trigger, clear: () => setTrigger('all') })
    return f
  }, [busquedaAplicada, product, soloDescartadas, soloMios, kind, minAdvertiserAds, minCopies, minDays, minVelocity, desdeHoras, status, trigger])

  /**
   * Cuántos filtros de detalle hay puestos. Es lo único que delata al panel cerrado, así
   * que cuenta EXACTAMENTE lo que vive dentro de él: si mañana un mando se sube a la fila
   * de arriba, tiene que salir también de aquí o el contador mentirá.
   */
  const afinados = [
    product !== 'digital',
    status !== 'all',
    trigger !== 'all',
    kind !== 'all',
    !!minAdvertiserAds,
    !!minCopies,
    !!minDays,
    !!minVelocity,
    !!desdeHoras
  ].filter(Boolean).length

  /**
   * Las preguntas de todos los días, cada una con la combinación que la contesta.
   *
   * Viven en una lista y no en botones sueltos porque cada una tiene que saber APAGAR lo
   * que puso otra: si "Explosivos" no limpiara los días, quedaría cruzado con "Probados" y
   * la rejilla enseñaría la intersección sin que nadie la haya pedido.
   */
  /**
   * El estado que gobiernan los atajos, leído y escrito de una pieza.
   *
   * Va junto porque los atajos son excluyentes: poner uno es asignar los SIETE campos, no
   * seis `set*` sueltos con la esperanza de no olvidar ninguno. La exclusividad se
   * comprueba en `atajosCapturas.check`.
   */
  const estadoAtajo: EstadoAtajo = {
    sortBy,
    minVelocity,
    minAdvertiserAds,
    minDays,
    minCopies,
    desdeHoras,
    papelera: soloDescartadas
  }

  const ponerEstado = (e: EstadoAtajo) => {
    setMinVelocity(e.minVelocity)
    setMinAdvertiserAds(e.minAdvertiserAds)
    setMinDays(e.minDays)
    setMinCopies(e.minCopies)
    setDesdeHoras(e.desdeHoras)

    // El tramo arrastra más cosas que un umbral —agrupado, orden, filtro de producto—, así
    // que lo pone quien sabe hacerlo en vez de repetirlo aquí.
    verTramo(e.papelera ? 'descartadas' : 'visibles')
    if (e.papelera) setSoloMios(true)

    // El orden va el ÚLTIMO a propósito: `verTramo` también lo toca, y lee el `sortBy` de
    // este render, que todavía es el de antes. Saliendo de la papelera vería 'dismissed' y
    // lo devolvería a 'velocity', pisando el orden que pide el atajo.
    setSortBy(e.sortBy)
  }

  const activo = atajoActivo(estadoAtajo)

  const limpiarTodo = () => {
    setBusqueda('')
    verTramo('visibles')
    setKind('all')
    setMinAdvertiserAds('')
    setMinVelocity('')
    setDesdeHoras('')
    setMinCopies('')
    setMinDays('')
    setStatus('all')
    setTrigger('all')
  }

  const pagination = React.useMemo(() => {
    const pages = (data as any)?.pages || []
    return pages[pages.length - 1]?.pagination
  }, [data])

  /**
   * Centinela del scroll infinito.
   *
   * Se observa un elemento al final de la rejilla y se pide la tanda siguiente cuando
   * asoma, con 600px de margen para que la carga empiece antes de llegar al fondo y no se
   * note el corte. Se comprueba `hasNextPage` e `isFetchingNextPage` dentro del callback
   * porque el observador puede disparar varias veces seguidas mientras se desplaza.
   */
  const centinela = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const nodo = centinela.current
    if (!nodo || !hasNextPage) return

    const obs = new IntersectionObserver(
      entradas => {
        if (entradas[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '600px' }
    )

    obs.observe(nodo)
    return () => obs.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const settings = settingsData?.settings

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-stone-100">
            <Flame className="h-6 w-6" style={{ color: '#fbbf24' }} />
            Capturas automáticas
          </h1>
          <p className="mt-1 text-sm text-stone-400">
            Anuncios que el sistema detectó en fuego durante tus búsquedas y guardó solo,
            con su evolución en el tiempo.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={invalidate}
            disabled={isFetching && !isFetchingNextPage}
            className="flex items-center gap-2 rounded-xl border border-stone-600 bg-stone-900 px-3 py-2 text-sm text-stone-200 hover:border-stone-500 disabled:opacity-60"
            title="Traer las capturas nuevas y recalcular los números"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching && !isFetchingNextPage ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          {!readOnly && (
            <button
              onClick={() => setShowSettings(v => !v)}
              className="flex items-center gap-2 rounded-xl border border-stone-600 bg-stone-900 px-3 py-2 text-sm text-stone-200 hover:border-stone-500"
            >
              <Settings2 className="h-4 w-4" />
              Umbrales
            </button>
          )}
        </div>
      </div>

      {stats && (
        // Embudo y fichas en una sola franja a todo el ancho: apilados y topados a max-w-4xl
        // dejaban media pantalla vacía y empujaban la rejilla hacia abajo.
        <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          {/* El embudo sustituye a la tarjeta "Anunciantes": decía cuántos hay, no por qué
              son tantos menos de los que el minero canta, que es lo que nadie entendía.
              Usa los mismos filtros que la rejilla, así que sus números siempre cuadran. */}
          <Embudo
            capturado={stats.captured ?? 0}
            descartadas={stats.dismissed ?? 0}
            pendientes={stats.pending ?? 0}
            visibles={stats.visible ?? 0}
            anunciantes={vista === 'visibles' && grouped ? stats.advertisers : undefined}
            vista={vista}
            onVer={verTramo}
          />

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile
              label={grouped ? 'En pantalla' : 'Capturados'}
              value={grouped ? stats.advertisers ?? 0 : stats.total}
              hint={
                grouped
                  ? `${stats.total} creativos entre todos`
                  : `de ${stats.advertisers ?? 0} anunciantes`
              }
            />
            <StatTile
              label="Subiendo"
              value={<span style={{ color: STATUS.rising.color }}>{stats.byStatus?.rising ?? 0}</span>}
              hint="ganando copias"
            />
            <StatTile label="Pico máximo" value={stats.maxPeak} hint="copias de un creativo" />
            <StatTile
              label="Media archivada"
              value={`${stats.mediaArchived}/${stats.total}`}
              hint="creativos a salvo"
            />
          </div>
        </div>
      )}

      {showSettings && settings && (
        <div className="rounded-xl border border-stone-700 bg-stone-900 p-4">
          <h2 className="mb-1 text-sm font-semibold text-stone-100">Qué cuenta como "en fuego"</h2>
          <p className="mb-4 text-xs text-stone-400">
            Basta con que se cumpla una de las reglas. Ojo: estos mismos umbrales deciden
            a qué anunciantes se les consulta el catálogo y a qué anuncios se les paga la
            transcripción, así que bajarlos gasta más créditos por búsqueda.
          </p>

          <form
            className="grid gap-4 md:grid-cols-3"
            onSubmit={e => {
              e.preventDefault()
              const form = new FormData(e.currentTarget)
              settingsMutation.mutate({
                minCollationCount: Number(form.get('minCollationCount')),
                minDaysRunning: Number(form.get('minDaysRunning')),
                minVelocity: Number(form.get('minVelocity')),
                longevityDays: Number(form.get('longevityDays')),
                longevityMinCollation: Number(form.get('longevityMinCollation')),
                enabled: form.get('enabled') === 'on',
                onlyDigitalProducts: form.get('onlyDigitalProducts') === 'on',
                analyzeWinners: form.get('analyzeWinners') === 'on',
                analyzeWinnersPeak: Number(form.get('analyzeWinnersPeak')),
                leaderEnabled: form.get('leaderEnabled') === 'on',
                leaderMinCollation: Number(form.get('leaderMinCollation')),
                directResponseEnabled: form.get('directResponseEnabled') === 'on',
                directResponseMinCollation: Number(form.get('directResponseMinCollation'))
              })
            }}
          >
            {[
              { key: 'minCollationCount', rule: 'Volumen', label: 'Copias mínimas', step: '1' },
              { key: 'minDaysRunning', rule: 'Volumen', label: 'Días mínimos activo', step: '1' },
              { key: 'minVelocity', rule: 'Velocidad', label: 'Copias ganadas por día', step: '0.1' },
              { key: 'longevityDays', rule: 'Longevidad', label: 'Días para evergreen', step: '1' },
              { key: 'longevityMinCollation', rule: 'Longevidad', label: 'Copias de un evergreen', step: '1' },
              {
                key: 'leaderMinCollation',
                rule: 'Líder del nicho',
                label: 'Copias mínimas para ser líder',
                step: '1'
              },
              {
                key: 'analyzeWinnersPeak',
                rule: 'Análisis',
                label: 'Copias para destripar lo capturado antes',
                step: '1'
              },
              {
                key: 'directResponseMinCollation',
                rule: 'Respuesta directa',
                label: 'Copias mínimas (mínimo 2)',
                step: '1'
              }
            ].map(f => (
              <label key={f.key} className="block">
                <span className="text-xs uppercase tracking-wide text-stone-500">{f.rule}</span>
                <span className="mb-1 block text-sm text-stone-300">{f.label}</span>
                <input
                  name={f.key}
                  type="number"
                  step={f.step}
                  min="0"
                  defaultValue={settings[f.key]}
                  className="w-full rounded-xl border border-stone-600 bg-stone-950 px-3 py-2 text-stone-100 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-500/30"
                />
              </label>
            ))}

            <div className="space-y-3 self-end md:col-span-2">
              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  name="enabled"
                  type="checkbox"
                  defaultChecked={settings.enabled}
                  className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
                />
                Captura automática activada
              </label>

              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  name="onlyDigitalProducts"
                  type="checkbox"
                  defaultChecked={settings.onlyDigitalProducts}
                  className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
                />
                <span>
                  Solo productos digitales
                  <span className="block text-xs text-stone-500">
                    Deja fuera apps, tiendas y servicios presenciales antes de guardarlos.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  name="leaderEnabled"
                  type="checkbox"
                  defaultChecked={settings.leaderEnabled}
                  className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
                />
                <span>
                  Capturar al que domina su nicho
                  <span className="block text-xs text-stone-500">
                    Ganar es relativo al mercado. Medido en seis nichos reales, la mediana de
                    copias es 1 en todos, y hay mercados cuyo mejor anunciante corre 3 copias:
                    con umbral fijo esos nichos son invisibles para siempre.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  name="directResponseEnabled"
                  type="checkbox"
                  defaultChecked={settings.directResponseEnabled ?? true}
                  className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
                />
                <span>
                  Capturar la respuesta directa por WhatsApp
                  <span className="block text-xs text-stone-500">
                    Ofertas que venden por conversación —«te lo envío y pagas después», «aporte
                    voluntario»— y no escalan en copias: medido, su mediana es 1 copia frente al
                    umbral general de 5. Entra solo la firma completa: destino de venta, la
                    mecánica en el texto y al menos 2 copias. Pasa igual por el clasificador.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-stone-300">
                <input
                  name="analyzeWinners"
                  type="checkbox"
                  defaultChecked={settings.analyzeWinners}
                  className="mt-0.5 h-4 w-4 rounded border-stone-600 bg-stone-950"
                />
                <span>
                  Destripar la oferta de cada captura que se queda
                  <span className="block text-xs text-stone-500">
                    Al confirmarse, extrae gancho, promesa, mecanismo y precio del guion del
                    vídeo, que el clasificador ya pidió. Una llamada a un modelo flash por
                    captura: la tarjeta nace completa.
                  </span>
                </span>
              </label>
            </div>

            <div className="md:col-span-3">
              <button
                type="submit"
                disabled={settingsMutation.isLoading}
                className="rounded-xl bg-secondary-600 px-4 py-2 text-sm font-medium text-white hover:bg-secondary-500 disabled:opacity-50"
              >
                {settingsMutation.isLoading ? 'Guardando…' : 'Guardar umbrales'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Una sola fila: buscar, los atajos, el orden y todo lo demás detrás de "Afinar".
          Antes había DIEZ mandos aquí, y cuatro de ellos —tamaño, copias, días y velocidad—
          solo servían para componer a mano lo que un atajo pone de un clic. El coste de
          plegarlos es nulo porque "Filtrando por" sigue enseñando lo que hay puesto: no se
          esconde el estado, se esconde el mando. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* El buscador va primero y ancho: es lo que se usa cuando ya sabes qué buscas,
            mientras que los atajos son para explorar. */}
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500" />
          <input
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar anunciante, oferta o término…"
            className="w-full rounded-xl border border-stone-600 bg-stone-900 py-2 pl-9 pr-9 text-sm text-stone-100 placeholder-stone-500 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-500/30"
          />
          {busqueda && (
            <button
              type="button"
              onClick={() => setBusqueda('')}
              title="Limpiar la búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-500 hover:text-stone-200"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Las preguntas que se hacen todos los días, a un clic. El orden por defecto
            (velocidad) enseña lo que más rápido crece, y el 74% lleva menos de 30 días: o
            sea promesas. Estos atajos llevan a lo que sí se puede modelar: lo que está
            explotando AHORA con una máquina detrás, y lo que lleva meses aguantando. */}
        {/* Las preguntas de todos los días, a un clic. Son excluyentes —"Explosivos" y
            "Probados" se contradicen—, así que van como grupo de una sola elección y no
            como cinco interruptores sueltos. Por debajo de 1280 px solo cabe el icono: el
            nombre sigue en el título y en la etiqueta accesible. */}
        <div role="group" aria-label="Atajos" className="flex flex-wrap items-center gap-1">
          {ATAJOS.map(a => {
            const puesto = activo === a.id
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={puesto}
                aria-label={a.label}
                title={a.hint}
                onClick={() => ponerEstado(puesto ? SIN_ATAJO : a.estado)}
                className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm transition-colors ${
                  puesto
                    ? 'border-amber-500/60 bg-amber-500/10 text-amber-300'
                    : 'border-stone-600 bg-stone-900 text-stone-300 hover:border-stone-500'
                }`}
              >
                <span aria-hidden="true">{a.icono}</span>
                <span className="hidden xl:inline">{a.label}</span>
              </button>
            )
          })}
        </div>

        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="rounded-xl border border-secondary-700 bg-stone-900 px-3 py-2 text-sm text-stone-100"
          title="Cómo se ordena"
        >
          {(soloDescartadas ? [ORDEN_DESCARTE, ...SORTS] : SORTS).map(s => (
            <option key={s.value} value={s.value}>
              ↓ {s.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setAfinarAbierto(v => !v)}
          aria-expanded={afinarAbierto}
          aria-controls="panel-afinar"
          title="Los filtros de detalle: tipo, tamaño, copias, días, ritmo, estado y motivo"
          className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
            afinados
              ? 'border-secondary-500 bg-secondary-600/10 text-secondary-200'
              : 'border-stone-600 bg-stone-900 text-stone-300 hover:border-stone-500'
          }`}
        >
          <Settings2 className="h-4 w-4" />
          Afinar
          {afinados > 0 && (
            <span className="rounded-full bg-secondary-600/30 px-1.5 text-xs tabular-nums">{afinados}</span>
          )}
        </button>

        <span className="ml-auto flex items-center gap-2 text-sm text-stone-500">
          {/* Solo el refresco de fondo. Cargar la tanda siguiente al desplazar ya se anuncia
              abajo, y verlo aquí además haría parpadear "actualizando" en cada scroll. */}
          {isFetching && !isLoading && !isFetchingNextPage && (
            <span className="flex items-center gap-1.5 text-xs">
              <span
                className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ background: '#e879f9' }}
              />
              actualizando
            </span>
          )}
          {/* Agrupando, el total son ANUNCIANTES, no capturas, y no coincide con la tarjeta
              "Capturados" de arriba. Decir de qué se está hablando evita que el número
              parezca un error. */}
          {pagination && items.length > 0 && (
            <span
              title={
                grouped
                  ? 'Con "Uno por anunciante" activo se cuenta un anunciante por tarjeta, no cada creativo capturado.'
                  : 'Capturas individuales, un creativo por tarjeta.'
              }
            >
              {items.length} de {pagination.total} {grouped ? 'anunciantes' : 'capturas'}
            </span>
          )}
        </span>
      </div>

      {/* El detalle, plegado. Cada mando lleva su pregunta encima: un desplegable que pone
          "Cualquier tamaño" no dice de qué tamaño habla hasta que lo abres. */}
      {afinarAbierto && (
        <div
          id="panel-afinar"
          className="grid gap-x-4 gap-y-3 rounded-xl border border-stone-700 bg-stone-900/60 p-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <Afinado label="Qué mira la rejilla">
            <select value={product} onChange={e => setProduct(e.target.value)} className={CAMPO}>
              <option value="digital">Solo productos digitales</option>
              <option value="all">Todo lo capturado</option>
              <option value="pending">Sin clasificar todavía</option>
            </select>
          </Afinado>

          <Afinado label="Tipo de producto">
            <select value={kind} onChange={e => setKind(e.target.value)} className={CAMPO}>
              <option value="all">Cualquier tipo</option>
              {KINDS.map(k => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </Afinado>

          {/* El tamaño del anunciante: 15 fuerte, 25 muy fuerte, 50 bestia, 100 animal. */}
          <Afinado label="Tamaño del anunciante">
            <select value={minAdvertiserAds} onChange={e => setMinAdvertiserAds(e.target.value)} className={CAMPO}>
              {TIER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Afinado>

          <Afinado label="Copias del creativo">
            <select value={minCopies} onChange={e => setMinCopies(e.target.value)} className={CAMPO}>
              <option value="">Cualquier número</option>
              {[3, 5, 10, 20, 40].map(n => (
                <option key={n} value={String(n)}>{n}+ copias</option>
              ))}
            </select>
          </Afinado>

          <Afinado label="Tiempo corriendo">
            <select value={minDays} onChange={e => setMinDays(e.target.value)} className={CAMPO}>
              <option value="">Lleve lo que lleve</option>
              {[7, 30, 90, 180].map(n => (
                <option key={n} value={String(n)}>{n}+ días</option>
              ))}
            </select>
          </Afinado>

          {/* Este mando no existía: el atajo "Explosivos" encendía el ritmo y solo se podía
              apagar desde el chip. Un filtro que se pone y no se quita es una trampa. */}
          <Afinado label="Ritmo al que copia">
            <select value={minVelocity} onChange={e => setMinVelocity(e.target.value)} className={CAMPO}>
              <option value="">A cualquier ritmo</option>
              {[1, 2, 5].map(n => (
                <option key={n} value={String(n)}>{n}+ copias al día</option>
              ))}
            </select>
          </Afinado>

          {/* El otro mando que no existía: el atajo "Nuevas de hoy" ponía la ventana y solo
              se podía quitar desde el chip. */}
          <Afinado label="Capturado hace">
            <select value={desdeHoras} onChange={e => setDesdeHoras(e.target.value)} className={CAMPO}>
              <option value="">Cuando sea</option>
              <option value="24">Menos de 24 h</option>
              <option value="72">Menos de 3 días</option>
              <option value="168">Menos de 7 días</option>
            </select>
          </Afinado>

          <Afinado label="Estado del anuncio">
            <select value={status} onChange={e => setStatus(e.target.value)} className={CAMPO}>
              <option value="all">Todos los estados</option>
              {(Object.keys(STATUS) as Status[]).map(s => (
                <option key={s} value={s}>{STATUS[s].label}</option>
              ))}
            </select>
          </Afinado>

          <Afinado label="Por qué se capturó">
            <select value={trigger} onChange={e => setTrigger(e.target.value)} className={CAMPO}>
              <option value="all">Cualquier motivo</option>
              {Object.entries(TRIGGER_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Afinado>

          <Afinado label="Cómo se cuenta">
            <label
              className="flex cursor-pointer items-center gap-2 text-sm text-stone-200"
              title="Una tarjeta por anunciante, la mejor, en vez de repetirlo tantas veces como creativos tenga"
            >
              <input
                type="checkbox"
                checked={grouped}
                onChange={e => setGrouped(e.target.checked)}
                className="h-4 w-4 rounded border-stone-600 bg-stone-950"
              />
              Uno por anunciante
            </label>
          </Afinado>

          {/* Solo en la papelera: fuera de ella no hay ninguna mano que elegir. */}
          {soloDescartadas && (
            <Afinado label="De quién fue la mano">
              <label
                className="flex cursor-pointer items-center gap-2 text-sm text-stone-200"
                title="Los descartes que pulsaste tú en la rejilla, sin los automáticos"
              >
                <input
                  type="checkbox"
                  checked={soloMios}
                  onChange={e => setSoloMios(e.target.checked)}
                  className="h-4 w-4 rounded border-stone-600 bg-stone-950"
                />
                Solo los que descarté yo
              </label>
            </Afinado>
          )}
        </div>
      )}

      {/* Qué hay puesto ahora mismo, y cómo quitarlo. Es lo que permite plegar los mandos
          sin esconder nada: la pregunta "¿por qué solo veo 12 tarjetas?" se contesta aquí,
          esté el panel abierto o cerrado. */}
      {filtrosActivos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-stone-500">Filtrando por</span>
          {filtrosActivos.map(f => (
            <button
              key={f.key}
              onClick={f.clear}
              title="Quitar este filtro"
              className="flex items-center gap-1.5 rounded-full border border-secondary-700 bg-secondary-600/10 px-3 py-1 text-xs text-secondary-200 hover:border-secondary-500 hover:bg-secondary-600/20"
            >
              {f.label}
              <XCircle className="h-3.5 w-3.5 opacity-70" />
            </button>
          ))}
          <button
            onClick={limpiarTodo}
            className="rounded-full px-2 py-1 text-xs text-stone-500 underline-offset-2 hover:text-stone-300 hover:underline"
          >
            limpiar todo
          </button>
        </div>
      )}

      {isLoading && <div className="py-12 text-center text-stone-400">Cargando capturas…</div>}

      {error && (
        <div className="rounded-xl border border-stone-700 bg-stone-900 p-6 text-center text-stone-300">
          No se pudieron cargar las capturas.
        </div>
      )}

      {/* Vacío por una búsqueda y vacío de verdad no son lo mismo: decir "todavía no hay
          capturas" cuando hay 500 y ninguna dice "xyz" es sencillamente falso. */}
      {!isLoading && !error && items.length === 0 && (
        <div className="rounded-xl border border-stone-700 bg-stone-900 p-10 text-center">
          {busquedaAplicada ? (
            <>
              <Search className="mx-auto mb-3 h-8 w-8 text-stone-600" />
              <p className="text-stone-200">Nada coincide con «{busquedaAplicada}»</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
                Se busca en el anunciante, el término que la encontró, la oferta y el texto del
                anuncio. Prueba con menos palabras, o quita los filtros de arriba.
              </p>
              <button
                onClick={() => setBusqueda('')}
                className="mt-4 rounded-xl bg-stone-800 px-4 py-2 text-sm text-stone-100 hover:bg-stone-700"
              >
                Limpiar la búsqueda
              </button>
            </>
          ) : (
            <>
              <Flame className="mx-auto mb-3 h-8 w-8 text-stone-600" />
              <p className="text-stone-200">Todavía no hay capturas</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
                La captura corre sola en cada búsqueda. Si llevas varias búsquedas sin que
                aparezca nada, tus umbrales pueden ser demasiado altos para el nicho: ábrelos
                en "Umbrales".
              </p>
            </>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {items.map((item: any) => {
          const ad = item.adData || {}
          return (
            <CapturedAdCard
              key={item._id}
              item={item}
              footer={
                <>
                  {/* Cada botón dice lo que hace con palabras: con solo iconos, quien no conoce
                      la herramienta no sabía qué abría la flecha ni qué borraba la papelera.
                      La acción principal (guardar / recuperar) ocupa la fila de arriba. */}
                  <div className="mt-auto flex flex-wrap gap-2 border-t border-stone-800 p-3">
                    {!readOnly && (
                      <button
                        onClick={() => setGuardando(item)}
                        disabled={!!item.promotedToSavedAdId}
                        className={`flex basis-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium disabled:cursor-default ${
                          item.promotedToSavedAdId
                            ? 'bg-emerald-900/30 text-emerald-300'
                            : 'bg-amber-500 text-stone-950 hover:bg-amber-400 disabled:opacity-60'
                        }`}
                        title="Guárdalo en tu lista de anuncios para tenerlo a mano"
                      >
                        {item.promotedToSavedAdId ? <Check className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                        {item.promotedToSavedAdId ? 'Guardado en mis anuncios' : 'Guardar en mis anuncios'}
                      </button>
                    )}
    
                    {!readOnly && item.dismissedAt && (
                      <button
                        onClick={() => restoreMutation.mutate(item._id)}
                        disabled={restoreMutation.isLoading}
                        className="flex basis-full items-center justify-center gap-1.5 rounded-lg bg-emerald-900/40 px-3 py-2 text-sm font-medium text-emerald-300 hover:bg-emerald-900/70 disabled:opacity-40"
                        title={`Devolver "${item.pageName}" a la rejilla`}
                      >
                        <Undo2 className="h-4 w-4" />
                        Recuperar
                      </button>
                    )}
    
                    {ad.ad_snapshot_url && (
                      <a
                        href={ad.ad_snapshot_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-stone-800 px-3 py-2 text-sm text-stone-200 hover:bg-stone-700"
                        title="Abre el anuncio original en la biblioteca de anuncios de Meta"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Ver en Meta
                      </a>
                    )}
    
                    {/* La acción hace lo que la tarjeta REPRESENTA.
                        Agrupando, la tarjeta es un anunciante —no un creativo—, así que descartar
                        se lleva todo lo suyo. Antes descartaba solo el creativo visible y la
                        tarjeta no desaparecía: mostraba el siguiente del mismo anunciante, y
                        había que pulsar tantas veces como creativos tuviera. */}
                    {!readOnly && !item.dismissedAt && (
                    <button
                      onClick={() => {
                        if (!grouped) return dismissMutation.mutate(item._id)
    
                        const n = item.siblingCount || 1
                        if (n === 1) return dismissAdvertiserMutation.mutate(item.pageId)
    
                        const aviso =
                          `¿Echar a "${item.pageName}"?
    
    ` +
                          `Se descartan sus ${n} capturas y el minero deja de pedirle el catálogo.`
                        if (window.confirm(aviso)) dismissAdvertiserMutation.mutate(item.pageId)
                      }}
                      disabled={dismissMutation.isLoading || dismissAdvertiserMutation.isLoading}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-stone-800 px-3 py-2 text-sm text-stone-300 hover:bg-rose-900/40 hover:text-rose-300 disabled:opacity-40"
                      title={
                        grouped
                          ? `Quitar a "${item.pageName}": sus ${item.siblingCount || 1} capturas y dejar de seguirlo`
                          : 'Quitar esta captura de la rejilla (queda en la papelera)'
                      }
                    >
                      {grouped ? <Ban className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                      {grouped && (item.siblingCount || 1) > 1 ? `No me interesa (${item.siblingCount})` : 'No me interesa'}
                    </button>
                    )}
                  </div>
                </>
              }
            />
          )
        })}
      </div>

      {/* Scroll infinito: el centinela pide la tanda siguiente al asomar. Sin esto la
          rejilla enseñaba 60 de 209 y las otras 149 eran inalcanzables, así que cambiar el
          orden solo reordenaba cuáles 60 te tocaban ver. */}
      {hasNextPage && (
        <div ref={centinela} className="py-8 text-center text-sm text-stone-500">
          {isFetchingNextPage ? 'Cargando más…' : ' '}
        </div>
      )}

      {!hasNextPage && items.length > 0 && pagination?.total > POR_PAGINA && (
        <p className="py-8 text-center text-sm text-stone-600">
          Eso es todo: {pagination.total} {grouped ? 'anunciantes' : 'capturas'}.
        </p>
      )}

      {guardando && (
        <SaveCaptureModal item={guardando} onClose={() => setGuardando(null)} onSaved={invalidate} />
      )}
    </div>
  )
}
